import React, { forwardRef } from 'react'
import { usePageDestinationAccess } from './usePageAccess'

export const localPageDestination = (href: string): string | undefined => {
  try {
    const url = new URL(href, window.location.href)
    return url.origin === window.location.origin
      ? url.pathname + url.search
      : undefined
  } catch {
    // Malformed user-provided citations must not break the containing page.
    return undefined
  }
}

// Native anchors (citations, markdown and links opening a new tab) follow the
// same page decision as router links. External URLs retain their own behavior.
export const PageAnchor = forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(({ href, children, onClick, ...props }, ref) => {
  const canOpen = usePageDestinationAccess()
  const destination = href ? localPageDestination(href) : undefined
  if (destination && !canOpen(destination)) {
    return (
      <span
        {...props}
        role={undefined}
        tabIndex={undefined}
        onKeyDown={undefined}
        onKeyUp={undefined}
        onMouseDown={undefined}
        style={{ ...props.style, cursor: 'default', textDecoration: 'none' }}
      >
        {children}
      </span>
    )
  }
  return (
    <a {...props} href={href} onClick={onClick} ref={ref}>
      {children}
    </a>
  )
})
PageAnchor.displayName = 'PageAnchor'
